import { PopperElement, Props } from 'tippy.js';
import { ModalHTMLElement } from './modal';
import { uniqid } from '../utils/utils';

export function initFormSubmitConfirmationModals(): void {
    // Prise en charge des boutons à l'intérieur d'un contenu Tippy
    document.querySelectorAll<PopperElement>('[data-tippy]').forEach($tippy => {
        if (typeof $tippy._tippy === 'undefined') {
            return;
        }

        const onMountParent = $tippy._tippy.props.onMount;
        $tippy._tippy.setProps({
            onMount(instance) {
                if (true !== (instance.reference as InitializableHTMLElement<Props>).confirmationModalInitialized) {
                    onMountParent(instance);

                    (instance.reference as InitializableHTMLElement<Props>).confirmationModalInitialized = true;
                    searchAndInitFormSubmitConfirmationModals(instance.popper as HTMLElement);
                }
            },
        });
    });

    searchAndInitFormSubmitConfirmationModals(document);
}

function searchAndInitFormSubmitConfirmationModals($parent: HTMLElement|Document): void {
    $parent.querySelectorAll<HTMLButtonElement|HTMLInputElement>('[data-confirm-modal]').forEach($submitButton => {
        new FormSubmitConfirmationModal($submitButton);
    });
}

type ModalType = {
    $el: ModalHTMLElement,
    $titleContainer: HTMLElement|null,
    $contentContainer: HTMLElement|null,
    $yesButton: HTMLButtonElement,
    $noButton: HTMLButtonElement|null,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface InitializableHTMLElement<T> extends HTMLElement {
    confirmationModalInitialized?: boolean;
}

/**
 * Cette classe permet d'afficher une modal de confirmation avant de soumettre un formulaire.
 * 
 * Ajoutez sur le bouton de soumission du formulaire l'attribut "data-confirm-modal"
 * avec pour valeur l'ID de la modal à afficher.
 * 
 * Ces attributs sont facultatifs et permettent de modifier les textes de la modal :
 * - "data-confirm-modal-title" - Titre de la modal
 * - "data-confirm-modal-content" - Contenu de la modal
 * - "data-confirm-modal-yes-label" - Texte du bouton pour confirmer l'action
 * - "data-confirm-modal-no-label" - Texte du bouton pour annuler l'action
 * 
 * Exemple :
 * <form>
 *      <button
 *          type="submit"
 *          data-confirm-modal="modal-confirm-action"
 *          data-confirm-modal-title="Mon titre"
 *          data-confirm-modal-content="Mon contenu HTML"
 *          data-confirm-modal-yes-label="Oui"
 *          data-confirm-modal-no-label="Non"
 *      >
 *          Mon action
 *      </button>
 * </form>
 * 
 * Le contenu de la modal doit contenir à minima le bouton de confirmation avec l'attribut "data-confirm-modal-yes".
 * Les autres attributs sont facultatifs :
 * - data-confirm-modal-title
 * - data-confirm-modal-content
 * - data-confirm-modal-no
 * 
 * Exemple :
 * <h3 data-confirm-modal-title>Mon titre</h3>
 * <div data-confirm-modal-content>Mon contenu</div>
 * <button data-confirm-modal-yes>Confirmer</button>
 * <button data-confirm-modal-no>Annuler</button>
 */
class FormSubmitConfirmationModal {
    id = uniqid();
    $submitButton: InitializableHTMLElement<HTMLButtonElement|HTMLInputElement>;
    $form: HTMLFormElement;
    modal: ModalType;
    modalTitle: string|null;
    modalContent: string|null;
    modalYesButtonLabel: string;
    modalNoButtonLabel: string|null;

    constructor($submitButton: HTMLButtonElement|HTMLInputElement) {
        this.$submitButton = $submitButton;
        const $form = this.$submitButton.closest('form');
        if (!$form) {
            throw new Error(`Le formulaire contenant le bouton est introuvable.`);
        }

        this.$form = $form.cloneNode() as HTMLFormElement;
        this.$form.innerHTML = $form.innerHTML;

        const modalId = $submitButton.dataset.confirmModal as string;
        const $modal = document.getElementById(modalId) as ModalHTMLElement;

        if (!$modal) {
            throw new Error(`La modal avec l'identifiant "${modalId}" n'existe pas.`);
        }

        this.modal = {
            $el: $modal,
            $titleContainer: $modal.querySelector<HTMLElement>('[data-confirm-modal-title]'),
            $contentContainer: $modal.querySelector<HTMLElement>('[data-confirm-modal-content]'),
            $yesButton: $modal.querySelector('[data-confirm-modal-yes]') as HTMLButtonElement,
            $noButton: $modal.querySelector<HTMLButtonElement>('[data-confirm-modal-no]'),
        };

        if (!this.modal.$yesButton) {
            throw new Error("Le bouton de confirmation de la modal n'existe pas.");
        }

        // Récupération des textes de la modal
        this.modalTitle = $submitButton.dataset.confirmModalTitle ??
            this.modal.$titleContainer?.innerHTML ?? null;
        this.modalContent = $submitButton.dataset.confirmModalContent ??
            this.modal.$contentContainer?.innerHTML ?? null;
        this.modalYesButtonLabel = $submitButton.dataset.confirmModalYesLabel ??
            ((Array.from(this.modal.$yesButton.children).pop() as HTMLElement) ?? this.modal.$yesButton).innerHTML;
        this.modalNoButtonLabel = $submitButton.dataset.confirmModalNoLabel ??
            ((Array.from(this.modal.$noButton?.children ?? []).pop() as HTMLElement) ?? this.modal.$noButton)?.innerHTML ?? null;

        // Ouverture de la modal de confirmation au clique sur le $submitButton
        if (true !== this.$submitButton.confirmationModalInitialized) {
            this.$submitButton.confirmationModalInitialized = true;

            this.$submitButton.addEventListener('click', evt => {
                evt.preventDefault();

                // Application des textes de la modal
                this.changeModalTexts();

                this.modal.$el.dataset.identifier = this.id;

                // Ouverture de la modal
                this.modal.$el.open();
            });
        }

        // Fermeture de la modal au clique sur le bouton "non"
        this.modal.$noButton?.addEventListener('click', () => {
            if (this.modal.$el.dataset.identifier === this.id) {
                this.modal.$el.close();
            }
        });

        // Soumission du formulaire au clique sur le bouton "oui"
        this.modal.$yesButton.addEventListener('click', () => {
            if (this.modal.$el.dataset.identifier === this.id) {
                document.body.appendChild(this.$form);
                this.$form.submit();
                this.$form.remove();
            }
        });
    }

    /**
     * Change les textes de la modal
     * 
     * @return {void}
     */
    changeModalTexts(): void {
        if (this.modal.$titleContainer) {
            this.modal.$titleContainer.innerHTML = this.modalTitle ?? '';
        }

        if (this.modal.$contentContainer) {
            this.modal.$contentContainer.innerHTML = this.modalContent ?? '';
        }

        if (this.modal.$noButton) {
            ((Array.from(this.modal.$noButton.children).pop() as HTMLElement) ?? this.modal.$noButton).innerHTML = this.modalNoButtonLabel ?? '';
        }

        ((Array.from(this.modal.$yesButton.children).pop() as HTMLElement) ?? this.modal.$yesButton).innerHTML = this.modalYesButtonLabel;
    }
}
